import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Ask7GIcon from "../../assets/navigations/Ask7g-icon.svg";
import Ask7GIconPurple from "../../assets/interaction-detail-page/selectedAsk7g_icon.svg";
import "../../styles/Extraction.css";
import StageIcon from "../../assets/interaction-detail-page/stage-icon.svg";
import WeightedScoreIcon from "../../assets/interaction-detail-page/weighted-score.svg";
import AdvancedRecommendIcon from "../../assets/interaction-detail-page/advance-recommend.svg";
import UserInteractionsService, {
  fetchGenerateTemplateBasedExtraction,
} from "../../services/interactions/Interactions";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { capitalizeFirstLetter } from "../../utils/utils";
import { GlobalContext } from "../../context/GlobalContext";

interface FrameworkAnalysisProps {
  interactionId?: string;
  frameworkType: "BANT" | "MEDDICC";
  interactionAssetId: string;
  groupId: string | null
}


const FrameworkAnalysis = ({
  interactionId,
  frameworkType,
  interactionAssetId,
  groupId,
}: FrameworkAnalysisProps) => {
  const storageKey = `framework_analysis_${interactionId}_${frameworkType}_${groupId || "no-group"}`;
  const SESSION_TIMEOUT_MS = 5 * 60 * 1000;
  const POLL_INTERVAL_MS = 10000;

  const readStoredSession = () => {
    try {
      const raw =
        sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const initialSession = readStoredSession();
  const hasValidInitialSession =
    initialSession?.status === "polling" &&
    initialSession?.expiresAt > Date.now()
      ? initialSession
      : null;

  const [loading, setLoading] = useState(
    Boolean(hasValidInitialSession)
  );
  const [analysisGenerated, setAnalysisGenerated] = useState(
    Boolean(hasValidInitialSession?.analysisGenerated)
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { showSnackbar } = useDisplaySnackbar();
  const [selectedStage, setSelectedStage] = useState<any>(hasValidInitialSession?.stage || null);
  const [analysisData, setAnalysisData] = useState<any>(hasValidInitialSession?.analysisData || null);
  const [isCheckingExistingAnalysis, setIsCheckingExistingAnalysis] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const requestKey = useMemo(
    () => `${interactionId || ""}|${frameworkType}|${groupId || "no-group"}`,
    [interactionId, frameworkType, groupId]
  );
  const { impersonationDetails } = useContext(GlobalContext);
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  const {
    isLoading: isStageLoading,
    isFetching: isStageFetching,
    isSuccess: isStageSuccesss,
    isError: isStageError,
    error: stageError,
    data: stageData,
  } = UserInteractionsService.useGetFrameworkStages(!!frameworkType);

  const stages = useMemo(() => {
    return (
      stageData?.frameworks?.find((item: any) => item.framework === frameworkType)
        ?.stages || []
    );
  }, [stageData, frameworkType]);
  const frameworkKeys = useMemo(
    () => stages.map((stage: any) => stage.key).filter(Boolean),
    [stages]
  );
  const selectedStageKey = selectedStage?.key || "";
  const pollingControllerRef = useRef<AbortController | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSessionRef = useRef<any>(hasValidInitialSession);
  const requestKeyRef = useRef(requestKey);

  const wait = (ms: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        if (signal) {
          signal.removeEventListener("abort", onAbort);
        }
        resolve();
      }, ms);

      const onAbort = () => {
        clearTimeout(timeoutId);
        reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
      };

      if (signal) {
        signal.addEventListener("abort", onAbort, { once: true });
      }
    });

  const readPendingSession = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const clearPendingSession = () => {
    pendingSessionRef.current = null;
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem("framework_analysis");
  };

  const persistPendingSession = (payload: any) => {
    pendingSessionRef.current = payload;
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  };

  const clearPollingArtifacts = () => {
    pollingControllerRef.current?.abort();
    pollingControllerRef.current = null;

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    requestKeyRef.current = requestKey;
    setLoading(false);
    clearPollingArtifacts();
  }, [requestKey]);

  const isCurrentRequest = (key: string) => key === requestKeyRef.current;

  const isAbortError = (error: any) =>
    error?.name === "AbortError" ||
    error?.code === "ERR_CANCELED" ||
    error?.message === "canceled" ||
    error?.message === "Aborted";

  useEffect(() => {
    if(isStageError) {
      showSnackbar("error", stageError?.response?.data?.error )
    }
  },[isStageLoading, isStageFetching, isStageSuccesss, isStageError, stageError, stageData, showSnackbar]);

  const generateTemplateBasedExtractionMutation =
    UserInteractionsService.usePostGenerateTemplateBasedExtraction();

  const isTruthyGenerateResponse = (response: any) =>
    response === true ||
    response?.data === true ||
    response?.success === true ||
    response?.data?.success === true;

  const hasReadyExtraction = (response: any) => {
    if (!response) return false;

    if (Array.isArray(response)) {
      return response.length > 0;
    }

    if (typeof response === "object") {
      return Object.keys(response).length > 0;
    }

    return Boolean(response);
  };

  const resolveExtractionResponse = (response: any, keys: string) => {
    if (!response) return { matchedKey: "", payload: null };

    if (response?.category?.scoring) {
      return { matchedKey: keys.split(",")[0] || "", payload: response };
    }

    const requestedKeys = keys
      .split(",")
      .map((key) => key.trim())
      .filter(Boolean);

    for (const key of requestedKeys) {
      if (response?.[key]) {
        return { matchedKey: key, payload: response[key] };
      }

      if (response?.data?.[key]) {
        return { matchedKey: key, payload: response.data[key] };
      }
    }

    if (response?.template_key) {
      const matchedKey = String(response.template_key);
      if (requestedKeys.includes(matchedKey)) {
        return {
          matchedKey,
          payload:
            response?.[matchedKey] ||
            response?.data?.[matchedKey] ||
            response,
        };
      }
    }

    if (requestedKeys.length === 1) {
      return { matchedKey: requestedKeys[0], payload: response };
    }

    return { matchedKey: "", payload: null };
  };

  const fetchExtractionOnce = async (
    keys: string,
  ) => {
    const response = await fetchGenerateTemplateBasedExtraction({
      interactionId: interactionId || "",
      groupId: groupId || "",
      keys,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    });

    return response;
  };

  const hasPendingPollSession = () => {
    const session = readPendingSession();

    if (!session) return null;

    if (!session?.expiresAt || session.expiresAt <= Date.now()) {
      clearPendingSession();
      return null;
    }

    return session;
  };

  const startPollingExtraction = async (
    stage: any,
    options?: { startedAt?: number; expiresAt?: number },
    requestKeyOverride: string = requestKeyRef.current
  ) => {
    if (!isCurrentRequest(requestKeyOverride)) {
      return false;
    }

    const startedAt = options?.startedAt || Date.now();
    const expiresAt = options?.expiresAt || startedAt + SESSION_TIMEOUT_MS;
    const controller = new AbortController();

    clearPollingArtifacts();
    pollingControllerRef.current = controller;

    persistPendingSession({
      status: "polling",
      startedAt,
      expiresAt,
      stage,
      frameworkType,
      interactionId,
      groupId: groupId || "",
    });

    const timeoutMs = Math.max(expiresAt - Date.now(), 0);
    pollingTimeoutRef.current = setTimeout(() => {
      if (!isCurrentRequest(requestKeyOverride)) {
        return;
      }

      controller.abort();
      clearPendingSession();
      setLoading(false);
      setIsCheckingExistingAnalysis(false);
      setAnalysisGenerated(false);
      setAnalysisData(null);
      showSnackbar("info", "Template analysis timed out");
    }, timeoutMs);

    try {
      while (Date.now() < expiresAt) {
        if (!isCurrentRequest(requestKeyOverride)) {
          return false;
        }

        const rawExtraction = await fetchExtractionOnce(
          stage.key,
        );
        const resolvedExtraction = resolveExtractionResponse(
          rawExtraction,
          stage.key
        );

        if (!isCurrentRequest(requestKeyOverride)) {
          return false;
        }

        if (hasReadyExtraction(resolvedExtraction.payload)) {
          clearPendingSession();
          setSelectedStage(stage);
          setAnalysisData(resolvedExtraction.payload);
          setAnalysisGenerated(true);
          setExpandedAccordion(false);
          setIsCheckingExistingAnalysis(false);
          setLoading(false);
          return true;
        }

        await wait(POLL_INTERVAL_MS, controller.signal);
      }
    } catch (error: any) {
      if (isAbortError(error) || controller.signal.aborted || !isCurrentRequest(requestKeyOverride)) {
        return false;
      }
      throw error;
    } finally {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (pollingControllerRef.current === controller) {
        pollingControllerRef.current = null;
      }
    }

    clearPendingSession();
    setLoading(false);
    setIsCheckingExistingAnalysis(false);
    return false;
  };

  const existingAnalysis = async (signal?: AbortSignal) => {
    setIsCheckingExistingAnalysis(true);

    try {
      if (!frameworkKeys.length) {
        clearPendingSession();
        setSelectedStage(null);
        setAnalysisData(null);
        setAnalysisGenerated(false);
        setExpandedAccordion(false);
        return false;
      }

      const rawExtraction = await fetchExtractionOnce(
        frameworkKeys.join(","),
      );

      if (signal?.aborted || !isCurrentRequest(requestKey)) return false;

      const resolvedExtraction = resolveExtractionResponse(
        rawExtraction,
        frameworkKeys.join(",")
      );

      if (!isCurrentRequest(requestKey)) {
        return false;
      }

      if (hasReadyExtraction(resolvedExtraction.payload)) {
        const matchedStage = stages.find(
          (stage: any) => stage.key === resolvedExtraction.matchedKey
        );

        clearPendingSession();
        setSelectedStage(matchedStage || null);
        setAnalysisData(resolvedExtraction.payload);
        setAnalysisGenerated(true);
        setExpandedAccordion(false);
        setIsCheckingExistingAnalysis(false);
        return true;
      }

      clearPendingSession();
      setSelectedStage(null);
      setAnalysisData(null);
      setAnalysisGenerated(false);
      setExpandedAccordion(false);
      return false;
    } catch (error: any) {
      if (!isAbortError(error)) {
        setAnalysisData(null);
        setAnalysisGenerated(false);
      }
      return false;
    } finally {
      if (!signal?.aborted) {
        setIsCheckingExistingAnalysis(false);
      }
    }
  };

  useEffect(() => {
    if (!stages.length) return;

    const controller = new AbortController();
    const session = hasPendingPollSession();

    if (session?.stage?.key) {
      const stageToResume = stages.find(
        (stage: any) => stage.key === session.stage.key
      );

      if (stageToResume) {
        setSelectedStage(stageToResume);
        setLoading(true);
        setAnalysisData(initialSession?.analysisData || null);
        setAnalysisGenerated(Boolean(initialSession?.analysisGenerated));
        setExpandedAccordion(false);
        setIsCheckingExistingAnalysis(false);

        void startPollingExtraction(stageToResume, {
          startedAt: session.startedAt,
          expiresAt: session.expiresAt,
        });

        return () => {
          controller.abort();
          clearPollingArtifacts();
          if (pendingSessionRef.current) {
            persistPendingSession(pendingSessionRef.current);
          }
        };
      }
    }

    setAnalysisData(null);
    setAnalysisGenerated(false);
    setExpandedAccordion(false);
    setIsCheckingExistingAnalysis(true);
    setSelectedStage(null);

    void existingAnalysis(controller.signal);

    return () => {
      controller.abort();
      clearPollingArtifacts();
      if (pendingSessionRef.current) {
        persistPendingSession(pendingSessionRef.current);
      }
    };
  }, [stages, frameworkKeys, frameworkType, interactionId, groupId]);

  useEffect(() => {
    return () => {
      clearPollingArtifacts();
      if (pendingSessionRef.current) {
        persistPendingSession(pendingSessionRef.current);
      }
    };
  }, []);

  const formatSectionTitle = (key: string) =>
    key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const handleGenerate = async () => {
    if (!selectedStage || loading) return;
    const currentRequestKey = requestKeyRef.current;

    try {
      setLoading(true);
      setAnalysisGenerated(false);
      setAnalysisData(null);
      setIsCheckingExistingAnalysis(false);

      const postResponse = await generateTemplateBasedExtractionMutation.mutateAsync({
        interactionId: interactionId || "",
        interactionAssetId,
        framework: frameworkType,
        stageId: selectedStage.id,
        key: selectedStageKey,
      });

      if (!isCurrentRequest(currentRequestKey)) {
        return;
      }

      if (!isTruthyGenerateResponse(postResponse)) {
        setLoading(false);
        showSnackbar("info", "Failed to generate analysis");
        return;
      }

      const result = await startPollingExtraction(selectedStage, {
        startedAt: Date.now(),
        expiresAt: Date.now() + SESSION_TIMEOUT_MS,
      }, currentRequestKey);

      if (!isCurrentRequest(currentRequestKey)) {
        return;
      }

      if (!result) {
        setLoading(false);
        return;
      }
    } catch (error: any) {
      setLoading(false);
      showSnackbar(
        "error",
        error?.response?.data?.error || "Failed to generate analysis"
      );
    }
  };

  const scoring = analysisData?.category?.scoring;
  const scoreEntries = scoring?.scores || {};
  const sectionOrder =
    frameworkType === "BANT"
      ? ["budget", "authority", "need", "timeline"]
      : [
          "metrics",
          "economic_buyer",
          "decision_criteria",
          "decision_process",
          "identify_pain",
          "champion",
          "competition",
        ];
  const meddiccLetterByKey: Record<string, string> = {
    metrics: "M",
    economic_buyer: "E",
    decision_criteria: "D",
    decision_process: "D",
    identify_pain: "I",
    champion: "C",
    competition: "C",
  };

  const analysisSections = sectionOrder
    .filter((key) => scoreEntries[key])
    .map((key) => {
      const section = scoreEntries[key];
      return {
        letter:
          frameworkType === "MEDDICC"
            ? meddiccLetterByKey[key] || key.charAt(0).toUpperCase()
            : key.charAt(0).toUpperCase(),
        title: formatSectionTitle(key),
        score: section?.score ?? 0,
        evidence: section?.evidence || "",
        missed: section?.gaps || "",
      };
    });

  const getScoreStyles = (score: number) => {
    if (score <= 3) {
      return {
        background: "#FFF1F1",
        color: "#D92D20",
        border: "1px solid #FECACA",
      };
    }

    if (score <= 6) {
      return {
        background: "#FEF6E9",
        color: "#AE751A",
        border: "1px solid #FCE3BB",
      };
    }

    return {
      background: "#E9FAF0",
      color: "#158F4C",
      border: "1px solid #B9EED1",
    };
  };

  const weightedScore = scoring?.weighted_score ?? "--";
  const advanceRecommendation =
    scoring?.next_steps?.advance_recommendation ||
    "--";

  return (
    <Box className="container">
      {isCheckingExistingAnalysis && (
        <Box className="intial-screen">
          <Typography className="intial-screen-text">
            Checking whether {frameworkType} analysis already exists...
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 2,
            }}
          >
            <CircularProgress size={50} thickness={3} />
          </Box>
        </Box>
      )}
      {!analysisGenerated && !isCheckingExistingAnalysis && (
        <Box className="intial-screen">
          <Typography className="intial-screen-text">
          Pick a deal stage to generate stage-aware{" "}
          <span style={{ color: "#7C3AED", fontWeight: 600 }}>
            {frameworkType}
          </span>{" "}
          insights from this call:
          </Typography>
          <Box
            className="bant-stage-trigger"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            >
            <img
              src={StageIcon}
              alt="stage"
              className="bant-stage-icon"
            />

            <Typography className="bant-stage-label">
              Stage :
            </Typography>

            <Typography className="bant-stage-value">
              {capitalizeFirstLetter(selectedStage?.stage) || "Select"}
            </Typography>
            <ExpandMoreIcon fontSize="small" sx={{color: "#A6A6B2"}}/>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
          >
            {stages.map((stage: any) => (
              <MenuItem
                className="bant-stage-option"
                key={`${frameworkType}-${stage.id}`}
                onClick={() => {
                  setSelectedStage(stage);
                  setAnalysisGenerated(false);
                  setAnalysisData(null);
                  setExpandedAccordion(false);
                  setAnchorEl(null);
                }}
              >
                <Box className="bant-option">
                  <Typography>
                    {stage.stage.charAt(0).toUpperCase() +
                      stage.stage.slice(1)}
                  </Typography>

                  <Box
                    className={`option-circle ${
                      selectedStage?.id === stage.id
                        ? "selected-circle"
                        : ""
                    }`}
                  />
                </Box>
              </MenuItem>
            ))}
          </Menu>

          {!loading ? (
          <Box className="generate-btn-outline">
          <Button
            variant="contained"
            className="fm-generate-btn"
            disabled={!selectedStage || !interactionAssetId || isImpersonating}
            onClick={handleGenerate}
            sx={{
                opacity: !selectedStage || !interactionAssetId || isImpersonating ? 0.4 : 1,
                transition: "opacity 0.3s ease",
            }}
          >
            <img
            src={Ask7GIcon}
            alt="Ask7G"
            className="fm-generate-btn-icon"
            />
            Generate {frameworkType}
          </Button>
          </Box>
          ) : (
          <Box
            sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 2,
            }}
          >
            <svg width="0" height="0" style={{ position: "absolute" }}>
            <defs>
              <linearGradient
              id="ask7gGradient"
              x1="2.52"
              y1="32.34"
              x2="33.6"
              y2="8.4"
              gradientUnits="userSpaceOnUse"
              >
              <stop offset="0.216892" stopColor="#35013A" stopOpacity="0.06" />
              <stop offset="0.995265" stopColor="#7F018B" />
              </linearGradient>
            </defs>
            </svg>

            <Box className="prep-notes-progress-box">
            <CircularProgress
              size={50}
              thickness={3}
              sx={{
              "& .MuiCircularProgress-circle": {
                  stroke: "url(#ask7gGradient)",
              },
              }}
            />

            <Box className="prep-notes-progress-icon-box">
              <img
              src={Ask7GIconPurple}
              alt={frameworkType}
              className="prep-notes-progress-icon"
              />
            </Box>
          </Box>

            <Typography
              className="prep-notes-generating-text"
              sx={{ marginTop: "16px" }}
              >
              Generating {frameworkType} Analysis...
            </Typography>
          </Box>
          )}
        </Box>
        )}
      {!loading && analysisGenerated && (
        <Box className="bant-analysis-container">
          <Box className="top-labels sticky-top-labels">
            <Box className="bant-summary-pill">
                <img
                    src={StageIcon}
                    alt="stage"
                    className="bant-stage-icon"
                />
                <Typography className="chip-label">
                    Stage :
                </Typography>
                <Typography className="stage-value">
                    {capitalizeFirstLetter(analysisData?.stage || selectedStage?.stage)}
                </Typography>
            </Box>

            <Box className="bant-summary-pill">
                <img
                    src={WeightedScoreIcon}
                    alt="weightedScore"
                    className="bant-stage-icon"
                />
                <Typography className="chip-label">
                    Weighted Score :
                </Typography>
                <Typography className="stage-value">
                  {weightedScore}
                </Typography>
            </Box>
            <Box className="bant-summary-pill">
              <img
                src={AdvancedRecommendIcon}
                alt="advance recommend"
                className="bant-stage-icon"
              />
              <Typography className="chip-label">
                  Advance Recommendation :
              </Typography>
              <Typography className="stage-value">
                  {advanceRecommendation}
              </Typography>
            </Box>
          </Box>

          <Box className="bant-content">
          {analysisSections.map((section) => (
            <Accordion className="accordion"
              key={section.title}
              expanded={expandedAccordion === section.title}
              onChange={(_, isExpanded) =>
                setExpandedAccordion(isExpanded ? section.title : false)
              }
            >
              <AccordionSummary
                className={`analysis-summary ${
                  expandedAccordion === section.title ? "expanded" : ""
                }`}
                expandIcon={<ExpandMoreIcon />}
              >
                <Box className="bant-letter">
                  {section.letter}
                </Box>

                <Typography className="title">
                  {section.title}
                </Typography>

                <Divider
                  orientation="vertical"
                  flexItem
                  className="divider-line"
                />

                <Box
                    className="score"
                    sx={getScoreStyles(section.score)}
                >
                    Score: [{section.score}]
                </Box>
              </AccordionSummary>

              <AccordionDetails className="accordion-details">
                <Box className="info-card reason-content">
                  <Typography className="card-header reason-text">
                    Reason:
                  </Typography>
                  <Typography className="content-text">
                    {section.evidence}
                  </Typography>
                </Box>

                  <Box className="info-card gap-content">
                    <Typography className="card-header gap-text">
                      Gaps:
                    </Typography>
                    <Typography className="content-text">
                        {section.missed}
                    </Typography>
                  </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FrameworkAnalysis;
